import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { GetOrderCashOutReport } from "./queries";
import { buildGetOrdersVariables } from "./helpers";
import moment from "moment";
import { ORDER_QUERY_LIMIT } from "../Orders/constants";
import { CreatePayout } from "./mutation";
import DefaultToaster from "../../atoms/DefaultToaster";
import { Intent } from "@blueprintjs/core";
import useInputState from "../../hooks/useInputState";
import useTutorialsState from "../../hooks/useTutorialsState";
import useTagAccount from "../../hooks/useTagAccount";

export default function useOrderCashOutReport() {
  const [sort, setSort] = useState({ field: "createdAt", direction: "asc" });
  const [cashOutStep, setCashOutStep] = useState(0);
  const [cashOurError, setCashOurError] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTopOffDialogOpen, setIsTopOffDialogOpen] = useState(false);
  const [isTopOffConfirmed, setIsTopOffConfirmed] = useState(false);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [topOffAmount, setTopOffAmount] = useInputState("");
  const [topOffDescription, setTopOffDescription] = useInputState("");
  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();

  const [createPayout] = useMutation(CreatePayout, {
    refetchQueries: [
      {
        query: GetOrderCashOutReport,
        ...buildGetOrdersVariables({ sort })
      }
    ]
  });

  const { loading, data, fetchMore, refetch, error } = useQuery(
    GetOrderCashOutReport,
    {
      ...buildGetOrdersVariables({ sort }),
      fetchPolicy: "network-only"
    }
  );

  function onSortPress(field, direction) {
    setSort({ field, direction });
  }

  const saveChanges = async () => {
    try {
      setCashOurError("");
      setMutationLoading(true);
      await createPayout();

      const hasCashedOut = getHasCompletedTutorial("Cash Out");

      if (!hasCashedOut) {
        await completeTutorial({
          variables: { name: "Cash Out", version: 1 }
        });

        await tagAccount("Cash Out Completed");
      }

      setIsDialogOpen(false);
      setCashOutStep(0);
      setMutationLoading(false);
      DefaultToaster.show({
        message: "Transaction Successfully Cashed Out!",
        icon: "tick",
        intent: Intent.SUCCESS
      });
    } catch (err) {
      setMutationLoading(false);
      setCashOurError(
        "An error occurred while performing Cash Out Transaction."
      );
    }
  };

  const payments = loading
    ? []
    : data.payments.map((payment) => ({
        ...payment,
        id: payment.order.id,
        name: payment.order.product.name,
        amount: payment.amountDue,
        status: payment.paid ? "Paid" : "UnPaid",
        createdAt: moment(payment.createdAt).format("MMM D, YYYY hh:mm A")
      }));

  const {
    loadingMore,
    onEndReached,
    setPreventRefetch
  } = useOrderListPagination({
    payments,
    isOrdersLoading: loading,
    fetchMore,
    sort
  });

  return {
    loadingMore,
    loading,
    sort,
    cashOutStep,
    setCashOutStep,
    topOffAmount,
    cashOurError,
    setTopOffAmount,
    isTopOffConfirmed,
    setIsTopOffConfirmed,
    topOffDescription,
    setTopOffDescription,
    isDialogOpen,
    setIsDialogOpen,
    isTopOffDialogOpen,
    setIsTopOffDialogOpen,
    setPreventRefetch,
    mutationLoading,
    refetch,
    onEndReached,
    saveChanges,
    setCashOurError,
    onSortPress,
    overviewReport: loading || error || !data ? undefined : data
  };
}

export function useOrderListPagination({
  orders,
  isLoading,
  filters,
  fetchMore,
  sort
}) {
  const [preventRefetch, setPreventRefetch] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const onEndReached = async () => {
    if (isLoading || preventRefetch || loadingMore) return;

    setLoadingMore(true);

    await fetchMoreOrders({
      orders,
      sort,
      filters,
      fetchMore,
      setPreventRefetch
    });

    setLoadingMore(false);
  };

  return {
    onEndReached,
    loadingMore,
    setPreventRefetch
  };
}

export const fetchMoreOrders = async ({
  orders,
  sort,
  filters,
  fetchMore,
  setPreventRefetch
}) => {
  await fetchMore({
    ...buildGetOrdersVariables({
      sort,
      filters,
      cursor: orders.slice(-1)[0]
    }),
    updateQuery: (previousResult, { fetchMoreResult }) => {
      if (fetchMoreResult.orders.length < ORDER_QUERY_LIMIT) {
        setPreventRefetch(true);
      }

      return {
        ...previousResult,
        orders: [...previousResult.orders, ...fetchMoreResult.orders]
      };
    }
  });
};
